import { CJK_FONT_VAR } from '../model/pattern-language';

export const PATTERN_FONT_IDS = [
    'dm-sans',
    'noto-sans',
    'fira-sans',
    'tinos',
    'gelasio',
    'cousine',
] as const;

export type PatternFontId = (typeof PATTERN_FONT_IDS)[number];

export type PatternFont = {
    id: PatternFontId;
    label: string;
    /** Bare face name, as passed to `document.fonts.load`. */
    family: string;
    /** Full CSS font-family stack stored in Lexical styles. */
    stack: string;
};

const CJK = `var(${CJK_FONT_VAR})`;

/** Bundled pattern fonts, in menu order. */
export const PATTERN_FONTS: readonly PatternFont[] = [
    { id: 'dm-sans', label: 'DM Sans', family: 'DM Sans', stack: `"DM Sans", ${CJK}, sans-serif` },
    { id: 'noto-sans', label: 'Noto Sans', family: 'Noto Sans', stack: `"Noto Sans", ${CJK}, sans-serif` },
    { id: 'fira-sans', label: 'Fira Sans', family: 'Fira Sans', stack: `"Fira Sans", ${CJK}, sans-serif` },
    { id: 'tinos', label: 'Tinos', family: 'Tinos', stack: `Tinos, ${CJK}, serif` },
    { id: 'gelasio', label: 'Gelasio', family: 'Gelasio', stack: `Gelasio, ${CJK}, serif` },
    { id: 'cousine', label: 'Cousine', family: 'Cousine', stack: `Cousine, ${CJK}, monospace` },
];

export const DEFAULT_PATTERN_FONT: PatternFont = PATTERN_FONTS[0];

/** Pre-v3 font-family menu values -> bundled font id. */
export const LEGACY_FONT_MAP: Readonly<Record<string, PatternFontId>> = {
    'system-ui, sans-serif': 'dm-sans',
    'Georgia, serif': 'gelasio',
    '"Times New Roman", Times, serif': 'tinos',
    'Verdana, Geneva, sans-serif': 'noto-sans',
    '"Trebuchet MS", Helvetica, sans-serif': 'fira-sans',
    '"Courier New", Courier, monospace': 'cousine',
};

/** Exact (whitespace-trimmed) stack match; null when not a bundled stack. */
export function patternFontForStack(value: string): PatternFont | null {
    const trimmed = value.trim();
    return PATTERN_FONTS.find(f => f.stack === trimmed) ?? null;
}

/** Bundled stack -> itself, legacy value -> mapped stack, anything else -> default stack. */
export function normalizeFontFamily(value: string | undefined): string {
    if (value == null) return DEFAULT_PATTERN_FONT.stack;
    const bundled = patternFontForStack(value);
    if (bundled != null) return bundled.stack;
    const legacyId = Object.hasOwn(LEGACY_FONT_MAP, value.trim())
        ? LEGACY_FONT_MAP[value.trim()]
        : undefined;
    const mapped = PATTERN_FONTS.find(f => f.id === legacyId);
    return (mapped ?? DEFAULT_PATTERN_FONT).stack;
}
